import { EXTENDED_DATA_SCHEMA_TYPES } from './types';
import { getFieldValue } from './fieldHelpers';
import moment from 'moment';

export const FOUNDING_FAMILY_DATE_FORMAT = 'D-MMMM-YYYY';
export const SUBSCRIPTION_GRACE_PERIOD_DAYS = 30;
const GRACE_PERIOD_FAILED_STATUSES = ['past_due', 'unpaid', 'incomplete', 'incomplete_expired'];
const ACTIVE_SUBSCRIPTION_STATUSES = ['active', 'trialing'];
const BACKGROUND_CHECK_FLOW_STATUSES = ['incomplete', 'active', 'trialing', 'past_due', 'unpaid'];

// Membership dates are calendar dates, not instants: they are parsed and rendered in UTC so
// that the same date is shown to every viewer regardless of their timezone.
const parseAccessDate = activeDate => {
  if (!activeDate) {
    return null;
  }

  if (activeDate instanceof Date || typeof activeDate === 'number') {
    return moment.utc(activeDate);
  }

  return moment.utc(
    activeDate,
    [FOUNDING_FAMILY_DATE_FORMAT, 'DD-MMMM-YYYY', 'D-MMM-YYYY', 'DD-MMM-YYYY', moment.ISO_8601],
    true
  );
};

export const getStoredFoundingFamilyDate = () =>
  moment().format(FOUNDING_FAMILY_DATE_FORMAT).toLowerCase();

export const getFoundingFamilyFreeAccess = value => {
  if (value && typeof value === 'object') {
    return {
      active: value.active === true,
      activeDate: value.activeDate || null,
    };
  }

  return {
    active: value === true,
    activeDate: null,
  };
};

// Founding Family Members invited on or before the cutoff were sold a fixed founding term that
// ends on the same day for everyone, no matter when within that window they joined.
export const FOUNDING_FAMILY_FIXED_TERM_CUTOFF = '2026-08-31';
export const FOUNDING_FAMILY_FIXED_TERM_END = '2027-08-31';
export const MEMBERSHIP_DATE_DISPLAY_FORMAT = 'MMMM D, YYYY';

const FOUNDING_FAMILY_NAME_PATTERN = /founding\s*family/i;

const formatMembershipDate = date =>
  date && date.isValid() ? date.clone().format(MEMBERSHIP_DATE_DISPLAY_FORMAT) : null;

const parseUnixSeconds = value => {
  const timestamp = Number(value);
  return Number.isFinite(timestamp) && timestamp > 0 ? moment.unix(timestamp).utc() : null;
};

/**
 * Whether the member is on one of the Founding Family tiers (free or the $399 annual plan).
 * The tier is recognised by the membership name they were invited/subscribed with, or by the
 * presence of a founding family free access record.
 */
export const isFoundingFamilyMembership = (publicData = {}, protectedData = {}) => {
  const membershipNames = [
    protectedData.stripeSubscription?.SubscriptionName,
    publicData.invitedMembership?.name,
  ];

  return (
    membershipNames.some(name => FOUNDING_FAMILY_NAME_PATTERN.test(`${name || ''}`)) ||
    Object.prototype.hasOwnProperty.call(publicData, 'foundingFamilyFreeAccess')
  );
};

/**
 * The date the member accepted their invite, as a UTC moment (or null when unknown).
 * `userCreatedAt` (currentUser.attributes.createdAt) is the fallback: the account is created
 * at the moment the invite is accepted.
 */
export const getInviteAcceptanceDate = (publicData = {}, protectedData = {}, userCreatedAt) => {
  const candidates = [
    getFoundingFamilyFreeAccess(publicData.foundingFamilyFreeAccess).activeDate,
    publicData.invitedMembership?.acceptedAt,
    publicData.acceptedAt,
    protectedData.acceptedAt,
    userCreatedAt,
  ];

  return (
    candidates
      .map(parseAccessDate)
      .find(parsed => parsed && parsed.isValid()) || null
  );
};

/**
 * The membership renewal / term end date as a formatted calendar date (UTC), or null when it
 * cannot be determined. The result depends only on stored member data - never on "today" - so
 * it stays the same on every load until the member's tier or term changes.
 *
 * - Founding Family Member invited on or before Aug 31, 2026 (inclusive): Aug 31, 2027, fixed.
 * - Any other founding member: invite acceptance date + 1 year.
 * - All other tiers: payment date + 1 year, or invite acceptance date + 1 year when no payment
 *   was taken on platform.
 */
export const getMembershipRenewalDate = (publicData = {}, protectedData = {}, userCreatedAt) => {
  const acceptanceDate = getInviteAcceptanceDate(publicData, protectedData, userCreatedAt);

  if (isFoundingFamilyMembership(publicData, protectedData)) {
    const cutoff = moment.utc(FOUNDING_FAMILY_FIXED_TERM_CUTOFF, 'YYYY-MM-DD').endOf('day');
    // Existing founding members all joined within the founding window; when no acceptance date
    // was ever recorded for them, the fixed founding term is still the correct answer.
    const withinFoundingWindow = !acceptanceDate || acceptanceDate.isSameOrBefore(cutoff);

    return withinFoundingWindow
      ? formatMembershipDate(moment.utc(FOUNDING_FAMILY_FIXED_TERM_END, 'YYYY-MM-DD'))
      : formatMembershipDate(acceptanceDate.clone().add(1, 'year'));
  }

  const stripeSubscription = protectedData.stripeSubscription || {};
  // Stripe's period end is the paid-through date, i.e. the payment date plus one interval.
  const periodEnd = parseUnixSeconds(stripeSubscription.current_period_end);

  if (periodEnd) {
    return formatMembershipDate(periodEnd);
  }

  const paymentDate = parseUnixSeconds(stripeSubscription.current_period_start);

  if (paymentDate) {
    return formatMembershipDate(paymentDate.clone().add(1, 'year'));
  }

  return acceptanceDate ? formatMembershipDate(acceptanceDate.clone().add(1, 'year')) : null;
};

export const isFoundingFamilyFreeAccessActive = value => {
  const access = getFoundingFamilyFreeAccess(value);

  if (!access.active) {
    return false;
  }

  if (value === true && !access.activeDate) {
    return true;
  }

  const parsedActiveDate = parseAccessDate(access.activeDate);

  if (!parsedActiveDate || !parsedActiveDate.isValid()) {
    return false;
  }

  return parsedActiveDate.clone().add(1, 'year').endOf('day').isSameOrAfter(moment());
};

export const getSubscriptionGracePeriod = value => {
  if (value && typeof value === 'object') {
    return {
      active: value.active === true,
      activeDate: value.activeDate || null,
    };
  }

  return {
    active: value === true,
    activeDate: null,
  };
};

export const getSubscriptionGracePeriodUntilDate = activeDate => {
  const parsedActiveDate = parseAccessDate(activeDate);
  const validActiveDate = parsedActiveDate && parsedActiveDate.isValid() ? parsedActiveDate : null;

  return validActiveDate
    ? validActiveDate.clone().add(SUBSCRIPTION_GRACE_PERIOD_DAYS, 'days').format('MMMM D, YYYY')
    : null;
};

export const isSubscriptionGracePeriodActive = value => {
  const gracePeriod = getSubscriptionGracePeriod(value);

  if (!gracePeriod.active) {
    return false;
  }

  const parsedActiveDate = parseAccessDate(gracePeriod.activeDate);

  if (!parsedActiveDate || !parsedActiveDate.isValid()) {
    return false;
  }

  return parsedActiveDate
    .clone()
    .add(SUBSCRIPTION_GRACE_PERIOD_DAYS, 'days')
    .endOf('day')
    .isSameOrAfter(moment());
};

export const hasMarketplaceAccess = (publicData = {}, protectedData = {}) => {
  const foundingFamilyAccessActive = isFoundingFamilyFreeAccessActive(
    publicData.foundingFamilyFreeAccess
  );

  if (foundingFamilyAccessActive) {
    return true;
  }

  const subscriptionStatus = `${publicData.subscriptionStatus ||
    protectedData.stripeSubscription?.status ||
    ''}`.toLowerCase();

  if (ACTIVE_SUBSCRIPTION_STATUSES.includes(subscriptionStatus)) {
    return true;
  }

  if (GRACE_PERIOD_FAILED_STATUSES.includes(subscriptionStatus)) {
    return isSubscriptionGracePeriodActive(publicData['30DaysGracePeriod']);
  }

  if (subscriptionStatus === 'canceled') {
    return false;
  }

  return !!(publicData.subscription || protectedData.subscription);
};

export const hasMembershipHistory = (publicData = {}, protectedData = {}) => {
  return !!(
    publicData.subscription ||
    protectedData.subscription ||
    publicData.subscriptionStatus ||
    protectedData.stripeSubscription ||
    Object.prototype.hasOwnProperty.call(publicData, 'foundingFamilyFreeAccess') ||
    Object.prototype.hasOwnProperty.call(publicData, '30DaysGracePeriod')
  );
};

export const needsBackgroundCheckFlow = (publicData = {}, protectedData = {}) => {
  const foundingFamilyAccessActive = isFoundingFamilyFreeAccessActive(
    publicData.foundingFamilyFreeAccess
  );
  const backgroundChecked = !!(
    protectedData.backgroundChecked ||
    publicData.backgroundChecked ||
    foundingFamilyAccessActive
  );

  if (backgroundChecked) {
    return false;
  }

  const subscriptionStatus = `${publicData.subscriptionStatus ||
    protectedData.stripeSubscription?.status ||
    ''}`.toLowerCase();

  return !!(
    publicData.subscription ||
    BACKGROUND_CHECK_FLOW_STATUSES.includes(subscriptionStatus)
  );
};

/**
 * Get the namespaced attribute key based on the specified extended data scope and attribute key
 * @param {*} scope extended data scope
 * @param {*} key attribute key in extended data
 * @returns a string containing the namespace prefix and the attribute name
 */
export const addScopePrefix = (scope, key) => {
  const scopeFnMap = {
    private: k => `priv_${k}`,
    protected: k => `prot_${k}`,
    public: k => `pub_${k}`,
    meta: k => `meta_${k}`,
  };

  const validKey = key.replace(/\s/g, '_');
  const keyScoper = scopeFnMap[scope];

  return !!keyScoper ? keyScoper(validKey) : validKey;
};

/**
 * Pick extended data fields from given form data.
 * Picking is based on extended data configuration for the user and target scope and user type.
 *
 * This expects submit data to be namespaced (e.g. 'pub_') and it returns the field without that namespace.
 * This function is used when form submit values are restructured for the actual API endpoint.
 *
 * Note: This returns null for those fields that are managed by configuration, but don't match target user type.
 *       These might exists if user swaps between user types before saving the user.
 *
 * @param {Object} data values to look through against userConfig.js and util/configHelpers.js
 * @param {String} targetScope Check that the scope of extended data the config matches
 * @param {String} targetUserType Check that the extended data is relevant for this user type.
 * @param {Object} userFieldConfigs Extended data configurations for user fields.
 * @returns Array of picked extended data fields from submitted data.
 */
export const pickUserFieldsData = (data, targetScope, targetUserType, userFieldConfigs) => {
  return userFieldConfigs.reduce((fields, field) => {
    const { key, userTypeConfig, scope = 'public', schemaType } = field || {};
    const namespacedKey = addScopePrefix(scope, key);

    const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
    const isTargetScope = scope === targetScope;
    const isTargetUserType =
      !userTypeConfig.limitToUserTypeIds || userTypeConfig.userTypeIds.includes(targetUserType);

    if (isKnownSchemaType && isTargetScope && isTargetUserType) {
      const fieldValue = getFieldValue(data, namespacedKey);
      return { ...fields, [key]: fieldValue };
    } else if (isKnownSchemaType && isTargetScope && !isTargetUserType) {
      // Note: this clears extra custom fields
      // These might exists if user swaps between user types before saving the user.
      return { ...fields, [key]: null };
    }
    return fields;
  }, {});
};

/**
 * Pick extended data fields from given extended data of the user entity.
 * Picking is based on extended data configuration for the user and target scope and user type.
 *
 * This returns namespaced (e.g. 'pub_') initial values for the form.
 *
 * @param {Object} data extended data values to look through against userConfig.js and util/configHelpers.js
 * @param {String} targetScope Check that the scope of extended data the config matches
 * @param {String} targetUserType Check that the extended data is relevant for this user type.
 * @param {Object} userFieldConfigs Extended data configurations for user fields.
 * @returns Array of picked extended data fields
 */
export const initialValuesForUserFields = (data, targetScope, targetUserType, userFieldConfigs) => {
  return userFieldConfigs.reduce((fields, field) => {
    const { key, userTypeConfig, scope = 'public', schemaType } = field || {};
    const namespacedKey = addScopePrefix(scope, key);

    const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
    const isTargetScope = scope === targetScope;
    const isTargetUserType =
      !userTypeConfig?.limitToUserTypeIds || userTypeConfig?.userTypeIds?.includes(targetUserType);

    if (isKnownSchemaType && isTargetScope && isTargetUserType) {
      const fieldValue = getFieldValue(data, key);
      return { ...fields, [namespacedKey]: fieldValue };
    }
    return fields;
  }, {});
};

/**
 * Returns props for custom user fields
 * @param {*} userFieldsConfig Configuration for user fields
 * @param {*} intl
 * @param {*} userType User type to restrict fields to. If none is passed,
 * only user fields applying to all user types are returned.
 * @param {*} isSignup Optional flag to determine whether the target context
 * is a signup form. Defaults to true.
 * @returns an array of props for CustomExtendedDataField: key, name,
 * fieldConfig, defaultRequiredMessage
 */
export const getPropsForCustomUserFieldInputs = (
  userFieldsConfig,
  intl,
  userType = null,
  isSignup = true
) => {
  return (
    userFieldsConfig?.reduce((pickedFields, fieldConfig) => {
      const { key, userTypeConfig, schemaType, scope, saveConfig = {} } = fieldConfig || {};
      const namespacedKey = addScopePrefix(scope, key);
      const showField = isSignup ? saveConfig.displayInSignUp : true;

      const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
      const isTargetUserType =
        !userTypeConfig?.limitToUserTypeIds || userTypeConfig?.userTypeIds?.includes(userType);
      const isUserScope = ['public', 'private', 'protected'].includes(scope);

      return isKnownSchemaType && isTargetUserType && isUserScope && showField
        ? [
          ...pickedFields,
          {
            key: namespacedKey,
            name: namespacedKey,
            fieldConfig: fieldConfig,
            defaultRequiredMessage: intl.formatMessage({
              id: 'CustomExtendedDataField.required',
            }),
          },
        ]
        : pickedFields;
    }, []) || []
  );
};
